import { useState, useEffect, useContext, useCallback } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { ArrowLeft, AlertTriangle, ShieldAlert, Loader2, Trash2, CheckCircle2, Wrench, RefreshCw, Eye, FileSearch } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const fmtNum = (n) => {
  const v = parseFloat(n);
  return isFinite(v) ? v.toLocaleString('en-IN', { maximumFractionDigits: 3 }) : '—';
};

function StockHealth({ poType = 'material' }) {
  const { API } = useContext(AppContext);
  const navigate = useNavigate();

  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('orphanLots');
  const [reconciling, setReconciling] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  // Monthly Report Audit (issue-2 scan across all items)
  const _now = new Date();
  const [auditMonth, setAuditMonth] = useState(_now.getMonth() + 1);
  const [auditYear, setAuditYear] = useState(_now.getFullYear());
  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);

  const loadReportAudit = useCallback(async (m, y) => {
    setAuditLoading(true);
    try {
      const mm = m ?? auditMonth;
      const yy = y ?? auditYear;
      const res = await axios.get(`${API}/stores/consumable-stock/report-audit?month=${mm}&year=${yy}`);
      setAuditData(res.data);
    } catch (e) {
      setAuditData({ items: [], total: 0, period: '?', error: e?.response?.data?.detail || 'Failed to load' });
    } finally {
      setAuditLoading(false);
    }
  }, [API, auditMonth, auditYear]);

  const fetchHealth = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await axios.get(`${API}/stores/consumable-stock/health?poType=${poType}`);
      setHealth(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load Stock Health diagnostic');
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  // Whenever the user switches to the Report Audit tab, ensure the data is loaded
  useEffect(() => {
    if (activeTab === 'reportAudit' && auditData === null) {
      loadReportAudit();
    }
  }, [activeTab, auditData, loadReportAudit]);

  const reconcile = async (dryRun = true) => {
    if (!dryRun) {
      const ok = window.confirm(
        '⚠️ This will:\n' +
        `• Delete ${health.summary.orphanLots} orphan lot row(s)\n` +
        `• Delete ${health.summary.orphanInventory} orphan stock inventory row(s)\n` +
        `• Strip bad lot-deductions from ${health.summary.orphanIssues} material issue(s) (issues themselves are KEPT for audit)\n` +
        `• Rewrite stock quantity on ${health.summary.stockDrift} drifted material(s) to match the surviving lot sum\n\n` +
        'This cannot be undone. Continue?'
      );
      if (!ok) return;
    }
    setReconciling(true);
    setLastResult(null);
    try {
      const res = await axios.post(`${API}/stores/consumable-stock/health/reconcile`, {
        dry_run: dryRun,
        poType,
        deleteOrphanLots: true,
        deleteOrphanInventory: true,
        stripOrphanIssueDeductions: true,
        fixDrift: true,
      });
      setLastResult(res.data);
      if (!dryRun) await fetchHealth();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Reconcile failed');
    } finally {
      setReconciling(false);
    }
  };

  // Update both per-row fix and global fix to reflect the new
  // non-destructive + reversible reconcile behavior — message users
  // explicitly that nothing is deleted and there's an Undo button.
  // Apply the recommended fix to a SINGLE drifted material only
  const fixSingleDrift = async (row) => {
    const ok = window.confirm(
      `Apply the recommended fix to "${row.materialName}" ONLY?\n\n` +
      `→ ${row.recommendedFix}\n\n` +
      `This is non-destructive — only updates the ERP quantity. ` +
      `Duplicate rows (if any) are soft-archived, never deleted. ` +
      `Every change is audit-logged and reversible via "Undo last reconcile".`
    );
    if (!ok) return;
    try {
      const res = await axios.post(`${API}/stores/consumable-stock/health/reconcile`, {
        dry_run: false,
        poType,
        fixDrift: true,
        materialFilter: row.materialName,
      });
      setLastResult(res.data);
      await fetchHealth();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Fix failed');
    }
  };

  // Undo the most recent reconcile batch — fully reverts every
  // stock_inventory row touched in that batch back to its pre-reconcile state.
  const undoLastReconcile = async () => {
    const batchId = lastResult?.reconcileBatchId;
    const label = batchId ? `batch ${batchId}` : 'the most recent reconcile';
    const ok = window.confirm(
      `Undo ${label}?\n\n` +
      `Every stock_inventory row touched in that reconcile will be ` +
      `restored to its EXACT pre-fix quantity. Soft-archived duplicates ` +
      `will be un-archived. This is a safe full-rewind.`
    );
    if (!ok) return;
    setReconciling(true);
    try {
      const body = batchId ? { batchId } : {};
      const res = await axios.post(`${API}/stores/consumable-stock/health/reconcile/undo`, body);
      alert(`Restored ${res.data?.restored || 0} stock rows. Click OK and refresh.`);
      setLastResult(null);
      await fetchHealth();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Undo failed');
    } finally {
      setReconciling(false);
    }
  };

  if (loading) return <div className="text-center py-12 text-gray-500">Loading stock health…</div>;
  if (error) return (
    <div className="text-center py-12">
      <p className="text-rose-600 mb-3">{error}</p>
      <button onClick={fetchHealth} className="text-[#1a237e] underline">Retry</button>
    </div>
  );

  const s = health.summary;
  const totalIssues = s.orphanLots + s.orphanInventory + s.orphanIssues + s.stockDrift;

  const tabs = [
    { key: 'orphanLots',      label: 'Orphan Lots',      count: s.orphanLots,      tone: 'rose'    },
    { key: 'orphanInventory', label: 'Orphan Inventory', count: s.orphanInventory, tone: 'orange'  },
    { key: 'orphanIssues',    label: 'Tainted Issues',   count: s.orphanIssues,    tone: 'amber'   },
    { key: 'stockDrift',      label: 'Stock Drift',      count: s.stockDrift,      tone: 'yellow'  },
    { key: 'reportAudit',     label: 'Monthly Report Audit', count: auditData?.total ?? '·', tone: 'sky' },
  ];

  return (
    <div className="space-y-6" data-testid="stock-health-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/stores/consumable-stock')}
                  className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft size={18} className="text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ShieldAlert size={22} className={totalIssues > 0 ? 'text-rose-600' : 'text-emerald-600'} />
              Consumable Stock Health
            </h1>
            <p className="text-gray-500 mt-1">Find &amp; fix data drift caused by historic GRN deletions and bulk imports.</p>
            <a href="/settings/data-integrity"
               className="inline-flex items-center gap-1 mt-1 text-xs text-indigo-700 hover:text-indigo-900 hover:underline"
               data-testid="link-naming-health">
              Need to merge near-duplicate item names (e.g. <em>&quot;Self Drill Screw 120mm&quot;</em> vs <em>&quot;120 mm&quot;</em>)? → Open Commodity Naming Health
            </a>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchHealth}
                  className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-1 text-sm"
                  data-testid="refresh-health">
            <RefreshCw size={14} /> Refresh
          </button>
          {totalIssues > 0 && (
            <>
              <button onClick={() => reconcile(true)} disabled={reconciling}
                      className="px-3 py-2 bg-indigo-50 text-[#1a237e] border border-indigo-200 rounded-lg hover:bg-indigo-100 text-sm flex items-center gap-1 disabled:opacity-60"
                      data-testid="dry-run-btn">
                <Eye size={14} /> Dry-run
              </button>
              <button onClick={() => reconcile(false)} disabled={reconciling}
                      className="px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm flex items-center gap-1 disabled:opacity-60"
                      data-testid="fix-all-btn">
                {reconciling ? <Loader2 size={14} className="animate-spin" /> : <Wrench size={14} />}
                {reconciling ? 'Working…' : 'Fix Everything'}
              </button>
            </>
          )}
          {lastResult && !lastResult.dryRun && lastResult.reconcileBatchId && (
            <button onClick={undoLastReconcile} disabled={reconciling}
                    className="px-3 py-2 bg-amber-50 text-amber-800 border border-amber-300 rounded-lg hover:bg-amber-100 text-sm flex items-center gap-1 disabled:opacity-60"
                    title="Reverts every change from the most recent Fix Everything / per-row fix"
                    data-testid="undo-reconcile-btn">
              <RefreshCw size={14} /> Undo last reconcile
            </button>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className={`rounded-xl border p-4 ${totalIssues > 0 ? 'bg-rose-50/50 border-rose-200' : 'bg-emerald-50/50 border-emerald-200'}`}>
        {totalIssues === 0 ? (
          <div className="flex items-center gap-2 text-emerald-700">
            <CheckCircle2 size={18} />
            <span className="font-medium">All clean!</span>
            <span className="text-sm text-emerald-600">No orphans or drift detected.</span>
          </div>
        ) : (
          <div className="flex items-start gap-2 text-rose-700">
            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
            <div>
              <p className="font-medium">{totalIssues} integrity issue{totalIssues === 1 ? '' : 's'} detected.</p>
              <p className="text-sm text-rose-600 mt-1">
                Use <strong>Dry-run</strong> to preview the cleanup (no DB writes), then click <strong>Fix Everything</strong> when you're ready to apply.
                {' '}<strong>This action is fully reversible</strong> — every change is audit-logged, duplicate rows are soft-archived (never deleted), and an <strong>Undo last reconcile</strong> button appears after the fix to roll everything back in one click.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Result panel */}
      {lastResult && (
        <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-4 text-sm text-blue-900" data-testid="reconcile-result">
          <p className="font-semibold mb-1">{lastResult.dryRun ? '🔍 Dry-run result (no changes made):' : '✅ Reconciliation applied:'}</p>
          <ul className="list-disc ml-6 space-y-0.5">
            <li>Orphan lots {lastResult.dryRun ? 'would be removed' : 'removed'}: <strong>{lastResult.deletedLots}</strong></li>
            <li>Orphan inventory rows {lastResult.dryRun ? 'would be removed' : 'removed'}: <strong>{lastResult.deletedInventory}</strong></li>
            <li>Material issues {lastResult.dryRun ? 'would be stripped of bad deductions' : 'with bad deductions stripped'}: <strong>{lastResult.fixedIssues}</strong></li>
            <li>Stock rows {lastResult.dryRun ? 'would be rewritten' : 'rewritten'} to match lot sums: <strong>{lastResult.rewrittenStockRows}</strong></li>
          </ul>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="flex flex-wrap gap-1 border-b border-gray-100 px-2 pt-2">
          {tabs.map((t) => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
                    className={`px-3 py-2 text-sm font-medium rounded-t-lg border-b-2 transition ${
                      activeTab === t.key
                        ? 'border-[#1a237e] text-[#1a237e] bg-indigo-50/30'
                        : 'border-transparent text-gray-500 hover:text-gray-800'
                    }`}
                    data-testid={`tab-${t.key}`}>
              {t.label}
              <span className={`ml-2 inline-block min-w-[1.5rem] text-xs rounded-full px-1.5 py-0.5 ${
                t.count > 0 ? `bg-${t.tone}-100 text-${t.tone}-700` : 'bg-gray-100 text-gray-500'
              }`}>{t.count}</span>
            </button>
          ))}
        </div>

        <div className="p-3 overflow-x-auto">
          {activeTab === 'orphanLots' && (
            <OrphanLotsTable rows={health.orphanLots} />
          )}
          {activeTab === 'orphanInventory' && (
            <OrphanInventoryTable rows={health.orphanInventory} />
          )}
          {activeTab === 'orphanIssues' && (
            <OrphanIssuesTable rows={health.orphanIssues} />
          )}
          {activeTab === 'stockDrift' && (
            <StockDriftTable rows={health.stockDrift} onFixSingle={fixSingleDrift} />
          )}
          {activeTab === 'reportAudit' && (
            <ReportAuditPanel
              month={auditMonth} year={auditYear} now={_now}
              data={auditData} loading={auditLoading}
              onMonth={(v) => { setAuditMonth(v); setAuditData(null); }}
              onYear={(v) => { setAuditYear(v); setAuditData(null); }}
              onLoad={() => loadReportAudit()}
            />
          )}
        </div>
      </div>

      {/* Helpful explainer */}
      <details className="bg-gray-50 rounded-lg p-4 text-sm text-gray-700">
        <summary className="cursor-pointer font-semibold text-gray-900">What does "Fix Everything" do?</summary>
        <ol className="list-decimal ml-6 mt-3 space-y-1.5">
          <li><strong>Orphan Lots</strong> — Removes lot rows whose source GRN has been deleted. These lots can never be reconciled back to a real document.</li>
          <li><strong>Orphan Inventory</strong> — Removes stock_inventory rows whose `lastGRN` reference points to a deleted GRN AND have no surviving lots.</li>
          <li><strong>Tainted Issues</strong> — Strips invalid `lotDeductions` from material-issue records but <em>keeps</em> the issue itself with a timestamped `orphanCleanedAt` marker so the audit history stays intact.</li>
          <li><strong>Stock Drift</strong> — Re-writes <code>stock_inventory.quantity</code> to match the audit-trail equation: <em>opening + GRN inflow − consumed = expected</em>. For bulk-uploaded materials (e.g. Stock Report import), the original opening is read from the <code>fyOpeningQty</code> field stamped at import time — that anchor is trusted, so the detector can now catch the case where ERP shows MORE than the audit trail justifies (e.g. a dispatch was created but ERP wasn&apos;t decremented). Legacy rows without an anchor still fall back to the lot-truth check. Each fix tags the row with <code>driftFixedAt</code> and is fully reversible via the Undo button.</li>
        </ol>
        <p className="mt-3 text-xs italic text-gray-500">All operations are idempotent — running them again will be a no-op once everything is clean.</p>
      </details>
    </div>
  );
}

function OrphanLotsTable({ rows }) {
  if (rows.length === 0) return <EmptyState label="No orphan lots." />;
  return (
    <table className="w-full text-sm" data-testid="orphan-lots-table">
      <thead className="bg-gray-50">
        <tr>
          <Th>Lot ID</Th><Th>Material</Th><Th>Deleted GRN</Th>
          <Th right>Received</Th><Th right>Remaining</Th><Th right>Rate</Th>
          <Th>Vendor</Th><Th>Invoice</Th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.map((r) => (
          <tr key={r.id} className="hover:bg-rose-50/30" data-testid={`orphan-lot-${r.id}`}>
            <Td mono>{r.id}</Td>
            <Td bold>{r.commodityName}</Td>
            <Td><span className="text-xs bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded font-mono">{r.grnNumber}</span></Td>
            <Td right>{fmtNum(r.receivedQty)} {r.unit}</Td>
            <Td right>{fmtNum(r.remainingQty)} {r.unit}</Td>
            <Td right>₹{fmtNum(r.rate)}</Td>
            <Td>{r.vendorName || '—'}</Td>
            <Td>{r.invoiceNo || '—'}</Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function OrphanInventoryTable({ rows }) {
  if (rows.length === 0) return <EmptyState label="No orphan stock_inventory rows." />;
  return (
    <table className="w-full text-sm" data-testid="orphan-inventory-table">
      <thead className="bg-gray-50">
        <tr>
          <Th>Material</Th><Th right>Quantity</Th><Th>Unit</Th>
          <Th>Last GRN (deleted)</Th><Th right>Rate</Th><Th>Source</Th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.map((r) => (
          <tr key={r.id} className="hover:bg-orange-50/30">
            <Td bold>{r.materialName}</Td>
            <Td right>{fmtNum(r.quantity)}</Td>
            <Td>{r.unit || '—'}</Td>
            <Td><span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-mono">{r.lastGRN}</span></Td>
            <Td right>₹{fmtNum(r.rate)}</Td>
            <Td>{r.source || '—'}</Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function OrphanIssuesTable({ rows }) {
  if (rows.length === 0) return <EmptyState label="No tainted material-issue records." />;
  return (
    <table className="w-full text-sm" data-testid="orphan-issues-table">
      <thead className="bg-gray-50">
        <tr>
          <Th>Issue ID</Th><Th>Material</Th><Th right>Qty</Th>
          <Th>Issue Date</Th><Th>Issued To</Th>
          <Th>Bad Lot-Deductions</Th>
          <Th>What's wrong & how to fix</Th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.map((r) => (
          <tr key={r.id} className="hover:bg-amber-50/30">
            <Td mono>{r.id}</Td>
            <Td bold>{r.materialName}</Td>
            <Td right>{fmtNum(r.quantity)}</Td>
            <Td>{r.issueDate}</Td>
            <Td>{r.issuedTo} ({r.department})</Td>
            <Td>
              <ul className="space-y-1">
                {r.badDeductions.map((d, i) => (
                  <li key={i} className="text-xs">
                    <div>
                      <span className="font-mono text-rose-700">{d.lotId}</span>
                      {' '}→ {fmtNum(d.deductedQty)} @ ₹{fmtNum(d.rate)}
                      {d.grnNumber && <span className="text-gray-500 ml-1">(GRN {d.grnNumber})</span>}
                    </div>
                    {d.reason && (
                      <div className="text-[10px] text-rose-600 italic mt-0.5">↳ {d.reason}</div>
                    )}
                  </li>
                ))}
              </ul>
            </Td>
            <Td>
              <div className="text-xs text-gray-700 leading-relaxed max-w-md">
                {r.remarks || (
                  <span className="text-gray-400 italic">
                    Material-issue references a deleted GRN or orphaned lot.
                  </span>
                )}
              </div>
            </Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function StockDriftTable({ rows }) {
  if (rows.length === 0) return <EmptyState label="No stock drift detected. Bulk-upload baselines are respected — only flagged when the ledger truly doesn't reconcile." />;

  const fmtSigned = (v) => {
    const n = parseFloat(v);
    if (!isFinite(n)) return '—';
    const abs = Math.abs(n).toLocaleString('en-IN', { maximumFractionDigits: 2 });
    if (Math.abs(n) < 0.001) return '0';
    return (n > 0 ? '+' : '−') + abs;
  };

  return (
    <div className="space-y-3">
      <div className="bg-blue-50/50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
        <strong>How the calculation works:</strong> &nbsp;
        <span className="text-amber-700 font-mono">Baseline</span> + <span className="text-emerald-700 font-mono">GRN inflow</span> − <span className="text-rose-700 font-mono">Consumed</span> = <span className="text-indigo-700 font-mono">Expected stock</span>. We compare that against the current <span className="text-purple-700 font-mono">ERP quantity</span>. If they disagree, this material is flagged. Duplicate inventory rows are detected and grouped — you won't see the same material twice anymore.
      </div>
      <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="stock-drift-table">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Material</th>
            <th className="px-3 py-2 text-right text-xs font-semibold text-amber-700 uppercase">Baseline<br/><span className="text-[10px] font-normal text-gray-500">(bulk)</span></th>
            <th className="px-3 py-2 text-center text-xs text-gray-400">+</th>
            <th className="px-3 py-2 text-right text-xs font-semibold text-emerald-700 uppercase">GRN<br/><span className="text-[10px] font-normal text-gray-500">(received)</span></th>
            <th className="px-3 py-2 text-center text-xs text-gray-400">−</th>
            <th className="px-3 py-2 text-right text-xs font-semibold text-rose-700 uppercase">Consumed<br/><span className="text-[10px] font-normal text-gray-500">(issued)</span></th>
            <th className="px-3 py-2 text-center text-xs text-gray-400">=</th>
            <th className="px-3 py-2 text-right text-xs font-semibold text-indigo-700 uppercase">Expected</th>
            <th className="px-3 py-2 text-right text-xs font-semibold text-purple-700 uppercase">ERP shows</th>
            <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700 uppercase">Difference</th>
            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Recommended fix</th>
            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">What went wrong</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((r, i) => (
            <tr key={i} className="hover:bg-yellow-50/30 align-top" data-testid={`stock-drift-row-${i}`}>
              <td className="px-3 py-2 font-medium text-gray-900">
                <div>{r.materialName}</div>
                <div className="flex flex-wrap gap-1 mt-0.5">
                  {r.isBulkUpload && (
                    <span className="text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded font-medium">Bulk-Upload</span>
                  )}
                  {r.baselineSource === 'fyOpeningQty' || r.baselineSource === 'marchOpening' ? (
                    <span className="text-[10px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded font-medium" title="Opening baseline taken from the stock-report import — trusted anchor">
                      anchor: {r.baselineSource}
                    </span>
                  ) : null}
                  {r.duplicateRowCount > 1 && (
                    <span className="text-[10px] bg-fuchsia-100 text-fuchsia-800 px-1.5 py-0.5 rounded font-medium">
                      {r.duplicateRowCount} duplicate rows
                    </span>
                  )}
                </div>
                {r.duplicateRowCount > 1 && (
                  <div className="mt-1 text-[10px] text-gray-500">
                    Variants: {(r.rowVariants || []).map((v) => `"${v}"`).join(', ')}
                  </div>
                )}
              </td>
              <td className="px-3 py-2 text-right text-amber-700 font-mono">{fmtNum(r.baseline)}</td>
              <td className="px-3 py-2 text-center text-gray-300">+</td>
              <td className="px-3 py-2 text-right text-emerald-700 font-mono">{fmtNum(r.grnInflow)}</td>
              <td className="px-3 py-2 text-center text-gray-300">−</td>
              <td className="px-3 py-2 text-right text-rose-700 font-mono">{fmtNum(r.consumed)}</td>
              <td className="px-3 py-2 text-center text-gray-300">=</td>
              <td className="px-3 py-2 text-right text-indigo-700 font-semibold font-mono">{fmtNum(r.expectedStock)}</td>
              <td className="px-3 py-2 text-right text-purple-700 font-semibold font-mono">{fmtNum(r.actualErpStock)}</td>
              <td className="px-3 py-2 text-right">
                <span className={`font-semibold font-mono ${Math.abs(r.difference) < 0.001 ? 'text-gray-400' : (r.difference > 0 ? 'text-rose-700' : 'text-amber-700')}`}>
                  {fmtSigned(r.difference)}
                </span>
                <div className="text-[10px] text-gray-400">{r.unit}</div>
              </td>
              <td className="px-3 py-2 text-xs text-gray-800 max-w-[260px]">
                {r.recommendedFix || '—'}
              </td>
              <td className="px-3 py-2 text-xs text-gray-600 max-w-[320px]">
                {r.diagnosis || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function ReportAuditPanel({ month, year, now, data, loading, onMonth, onYear, onLoad }) {
  return (
    <div data-testid="report-audit-panel">
      <div className="flex flex-wrap items-end gap-3 mb-4 p-3 bg-sky-50/40 border border-sky-200 rounded-lg">
        <div className="flex-1 min-w-[200px]">
          <p className="text-sm font-medium text-sky-900 flex items-center gap-1.5">
            <FileSearch size={14} /> Find every item whose opening balance differs between OLD (buggy) and NEW (fixed) monthly-report formulas
          </p>
          <p className="text-xs text-sky-700 mt-1">Pick a month, then click <strong>Scan</strong>. Items where the original DECOATING-style double-count occurred will appear below.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={month} onChange={(e) => onMonth(Number(e.target.value))}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white" data-testid="audit-month">
            {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
              <option key={m} value={i + 1}>{m}</option>
            ))}
          </select>
          <select value={year} onChange={(e) => onYear(Number(e.target.value))}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white" data-testid="audit-year">
            {Array.from({ length: 7 }, (_, i) => now.getFullYear() - 5 + i).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button onClick={onLoad} disabled={loading}
                  className="px-3 py-2 bg-sky-600 text-white rounded-lg text-sm font-medium hover:bg-sky-700 disabled:opacity-60 flex items-center gap-1"
                  data-testid="audit-scan-btn">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <FileSearch size={14} />}
            {loading ? 'Scanning…' : 'Scan'}
          </button>
        </div>
      </div>

      {loading && <div className="text-center text-gray-500 py-8">Scanning {month}/{year}…</div>}
      {!loading && data && (
        data.total === 0 ? (
          <div className="text-center text-emerald-700 py-8 bg-emerald-50/50 border border-emerald-200 rounded-lg">
            <CheckCircle2 className="mx-auto mb-2" size={28} />
            <p className="font-medium">No items affected in {data.period}.</p>
            <p className="text-sm text-emerald-600 mt-1">The monthly report for this period was always correct.</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-700 mb-3">
              <strong className="text-sky-700">{data.total}</strong> item{data.total === 1 ? '' : 's'} affected during <strong>{data.period}</strong> — opening balance is now correctly computed. Items listed in descending order of impact.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="report-audit-table">
                <thead className="bg-gray-50">
                  <tr>
                    <Th>Item</Th>
                    <Th right>Current Stock</Th>
                    <Th right>Month Purchase</Th>
                    <Th right>Month Cons.</Th>
                    <Th right>OLD Opening</Th>
                    <Th right>NEW Opening</Th>
                    <Th right>Δ Opening</Th>
                    <Th right>OLD Closing</Th>
                    <Th right>NEW Closing</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.items.map((it, i) => (
                    <tr key={i} className="hover:bg-sky-50/30" data-testid={`audit-row-${i}`}>
                      <Td bold>{it.itemName}</Td>
                      <Td right>{fmtNum(it.currentStock)} {it.unit}</Td>
                      <Td right>{fmtNum(it.monthPurchase)}</Td>
                      <Td right>{fmtNum(it.monthConsumption)}</Td>
                      <Td right><span className="text-rose-700">{fmtNum(it.oldFormula.opening)}</span></Td>
                      <Td right><span className="text-emerald-700 font-medium">{fmtNum(it.newFormula.opening)}</span></Td>
                      <Td right>
                        <span className={it.openingDelta > 0 ? 'text-rose-700 font-semibold' : 'text-amber-700'}>
                          {it.openingDelta > 0 ? '−' : '+'}{fmtNum(Math.abs(it.openingDelta))}
                        </span>
                      </Td>
                      <Td right><span className="text-rose-700">{fmtNum(it.oldFormula.closing)}</span></Td>
                      <Td right><span className="text-emerald-700 font-medium">{fmtNum(it.newFormula.closing)}</span></Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )
      )}
    </div>
  );
}

function Th({ children, right }) {
  return (
    <th className={`px-3 py-2 text-xs font-semibold text-gray-600 uppercase ${right ? 'text-right' : 'text-left'}`}>
      {children}
    </th>
  );
}

function Td({ children, right, mono, bold }) {
  return (
    <td className={`px-3 py-2 ${right ? 'text-right' : 'text-left'} ${mono ? 'font-mono text-xs' : ''} ${bold ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
      {children}
    </td>
  );
}

function EmptyState({ label }) {
  return (
    <div className="text-center text-gray-500 py-8 text-sm">{label}</div>
  );
}

export default StockHealth;
